import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  Users, Search, FileText, Tag, Scan, BarChart, LogOut, User, Upload, 
  Trash2, UserCog, Calendar, Mail, Send, Clock, MessageCircle, BookOpen, 
  Inbox, DollarSign, ChevronDown, ChevronRight, Home, Settings, Building,
  AlertTriangle, CheckCircle, XCircle, TrendingUp, Menu, X, Download, Key, Package
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardNew = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expandedMenus, setExpandedMenus] = useState(['members', 'advertisers', 'billing', 'whatsapp', 'reports']);
  const [stats, setStats] = useState(null);
  const [waStats, setWaStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [username, setUsername] = useState('');
  const [role, setRole] = useState('');

  useEffect(() => {
    const storedUsername = localStorage.getItem('username');
    const storedRole = localStorage.getItem('role');
    setUsername(storedUsername || 'User');
    setRole(storedRole || '');
    fetchDashboardStats();
    fetchWhatsAppStats();
  }, []);

  const fetchDashboardStats = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats({
        aabkari: { active: 0, inactive: 0, expired: 0, expiring_soon: 0 },
        sugar: { active: 0, inactive: 0, expired: 0, expiring_soon: 0 },
        advertisers: { aabkari_active: 0, aabkari_inactive: 0, sugar_active: 0, sugar_inactive: 0 }
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchWhatsAppStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/whatsapp-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWaStats(response.data);
    } catch (error) {
      console.error('Error fetching WhatsApp stats:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('role');
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const toggleMenu = (menu) => {
    setExpandedMenus(prev => 
      prev.includes(menu) ? prev.filter(m => m !== menu) : [...prev, menu]
    );
  };

  const menuItems = [
    {
      id: 'members',
      title: 'Members',
      icon: <Users size={20} />,
      children: [
        { title: 'Register Member', path: '/register', icon: <User size={16} /> },
        { title: 'Search Members', path: '/search', icon: <Search size={16} /> },
        { title: 'Print Labels', path: '/print-label', icon: <Tag size={16} /> },
        { title: 'Member Reports', path: '/reports', icon: <BarChart size={16} /> },
      ]
    },
    {
      id: 'advertisers',
      title: 'Advertisers',
      icon: <Building size={20} />,
      children: [
        { title: 'Register Advertiser', path: '/register-advertiser', icon: <User size={16} /> },
        { title: 'Search Advertisers', path: '/search-advertiser', icon: <Search size={16} /> },
        { title: 'Print Labels', path: '/print-advertiser-label', icon: <Tag size={16} /> },
        { title: 'Advertiser Reports', path: '/advertiser-reports', icon: <BarChart size={16} /> },
      ]
    },
    {
      id: 'dispatch',
      title: 'Dispatch',
      icon: <Scan size={20} />,
      children: [
        { title: 'Scan Dispatch', path: '/scan-dispatch', icon: <Scan size={16} /> },
        { title: 'Dispatch Reports', path: '/dispatch-reports', icon: <BarChart size={16} /> },
        { title: 'Dispatch Confirmation', path: '/dispatch-confirmation', icon: <MessageCircle size={16} /> },
        { title: 'Mark Failed Courier', path: '/mark-failed-courier', icon: <AlertTriangle size={16} /> },
        { title: 'Courier Export', path: '/courier-export', icon: <Package size={16} /> },
      ]
    },
    {
      id: 'billing',
      title: 'Billing',
      icon: <FileText size={20} />,
      children: [
        // Proforma Invoice section
        { title: '— Proforma Invoices —', path: '#', icon: <FileText size={16} />, isHeader: true },
        { title: 'Create Proforma Invoice', path: '/create-proforma-invoice', icon: <FileText size={16} /> },
        { title: 'PI Reports', path: '/proforma-invoice-reports', icon: <BarChart size={16} /> },
        { title: 'Print PI', path: '/print-bill?type=pi', icon: <FileText size={16} /> },
        { title: 'Print PI (New)', path: '/print-bill-new?type=pi', icon: <FileText size={16} /> },
        // Bills section
        { title: '— Bills —', path: '#', icon: <FileText size={16} />, isHeader: true },
        { title: 'Enter Bill', path: '/enter-bill', icon: <FileText size={16} /> },
        { title: 'Bill Reports', path: '/bill-reports', icon: <BarChart size={16} /> },
        { title: 'Print Bill', path: '/print-bill?type=bill', icon: <FileText size={16} /> },
        { title: 'Print Bill (New)', path: '/print-bill-new?type=bill', icon: <FileText size={16} /> },
      ]
    },
    {
      id: 'email',
      title: 'Email Campaigns',
      icon: <Mail size={20} />,
      children: [
        { title: 'Monthly Magazine', path: '/monthly-magazine-email', icon: <BookOpen size={16} /> },
        { title: 'Send Campaign', path: '/send-campaign', icon: <Send size={16} /> },
        { title: 'Campaign Reports', path: '/campaign-reports', icon: <BookOpen size={16} /> },
        { title: 'Newsletter', path: '/newsletter', icon: <Send size={16} /> },
        { title: 'Renewal Reminders', path: '/renewal-reminders', icon: <Clock size={16} /> },
        { title: 'Ad Proposals', path: '/advertisement-proposal', icon: <Send size={16} /> },
        { title: 'Greetings Email', path: '/greetings-email', icon: <Mail size={16} /> },
        { title: 'Email Reports', path: '/email-reports', icon: <BarChart size={16} /> },
      ]
    },
    {
      id: 'whatsapp',
      title: 'WhatsApp Campaigns',
      icon: <MessageCircle size={20} />,
      children: [
        { title: 'Monthly Magazine', path: '/whatsapp-template-send', icon: <BookOpen size={16} /> },
        { title: 'Renewal Reminder', path: '/whatsapp-renewal', icon: <Clock size={16} /> },
        { title: 'WhatsApp Inbox', path: '/whatsapp-inbox', icon: <Inbox size={16} /> },
        { title: 'Delivery Reports', path: '/whatsapp-reports', icon: <BarChart size={16} /> },
        { title: 'Opt-in Page', path: '/whatsapp-optin', icon: <CheckCircle size={16} /> },
      ]
    },
    {
      id: 'reports',
      title: 'Reports',
      icon: <BarChart size={20} />,
      children: [
        { title: 'Delivery Failures', path: '/delivery-failures', icon: <AlertTriangle size={16} /> },
        { title: 'Email Reports', path: '/email-reports', icon: <Mail size={16} /> },
        { title: 'Bill Reports', path: '/bill-reports', icon: <FileText size={16} /> },
        { title: 'Dispatch Reports', path: '/dispatch-reports', icon: <Scan size={16} /> },
        { title: 'Ad Proposal Reports', path: '/ad-proposal-reports', icon: <FileText size={16} /> },
      ]
    },
    {
      id: 'admin',
      title: 'Admin Tools',
      icon: <Settings size={20} />,
      adminOnly: true,
      children: [
        { title: 'Manage Users', path: '/manage-users', icon: <UserCog size={16} /> },
        { title: 'Tariff Management', path: '/tariff-management', icon: <DollarSign size={16} /> },
        { title: 'Data Export / Backup', path: '/data-export', icon: <Download size={16} /> },
        { title: 'Data Health Check', path: '/data-health-check', icon: <AlertTriangle size={16} /> },
        { title: 'Migrate WhatsApp Nos.', path: '/migrate-whatsapp', icon: <MessageCircle size={16} /> },
        { title: 'Upload Members', path: '/upload-members', icon: <Upload size={16} /> },
        { title: 'Upload Advertisers', path: '/upload-advertisers', icon: <Upload size={16} /> },
        { title: 'Migrate Dates', path: '/migrate-dates', icon: <Calendar size={16} /> },
        { title: 'Delete Records', path: '/delete-records', icon: <Trash2 size={16} /> },
      ]
    },
    {
      id: 'account',
      title: 'My Account',
      icon: <User size={20} />,
      children: [
        { title: 'Change My Password', path: '/change-my-password', icon: <Key size={16} /> },
      ]
    },
  ];

  const StatCard = ({ title, value, subtitle, color, icon }) => (
    <div className="bg-white rounded-lg shadow-sm border p-4 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-3xl font-bold mt-1" style={{ color }}>{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className="p-3 rounded-full" style={{ backgroundColor: `${color}20` }}>
          {icon}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-64' : 'w-0'} bg-white shadow-lg transition-all duration-300 overflow-hidden flex flex-col`}>
        {/* Logo */}
        <div className="p-4 border-b bg-gradient-to-r from-blue-600 to-blue-700">
          <h1 className="text-xl font-bold text-white">Magazine CRM</h1>
          <p className="text-xs text-blue-200">Aabkari & Sugar Times</p>
        </div>

        {/* Menu */}
        <nav className="flex-1 overflow-y-auto p-2">
          {/* Dashboard Link */}
          <button
            onClick={() => navigate('/')}
            className={`w-full flex items-center px-3 py-2 mb-2 rounded-lg text-left transition-colors ${
              location.pathname === '/' || location.pathname === '/dashboard'
                ? 'bg-blue-50 text-blue-700'
                : 'hover:bg-gray-100 text-gray-700'
            }`}
          >
            <Home size={20} className="mr-3" />
            <span className="font-medium">Dashboard</span>
          </button>

          {menuItems.filter(menu => !menu.adminOnly || role === 'admin').map((menu) => (
            <div key={menu.id} className="mb-1">
              <button
                onClick={() => toggleMenu(menu.id)}
                data-testid={`sidebar-menu-${menu.id}`}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-gray-100 text-gray-700"
              >
                <div className="flex items-center">
                  {menu.icon}
                  <span className="ml-3 font-medium">{menu.title}</span>
                </div>
                {expandedMenus.includes(menu.id) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
              
              {expandedMenus.includes(menu.id) && (
                <div className="ml-4 mt-1 space-y-1">
                  {menu.children.map((child) => (
                    child.isHeader ? (
                      <div
                        key={child.title}
                        data-testid={`sidebar-section-header-${child.title.replace(/—/g, '').trim().replace(/\s+/g, '-').toLowerCase()}`}
                        className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400 select-none"
                      >
                        {child.title.replace(/—/g, '').trim()}
                      </div>
                    ) : (
                      <button
                        key={child.path}
                        onClick={() => navigate(child.path)}
                        data-testid={`sidebar-link-${child.title.trim().replace(/\s+/g, '-').toLowerCase()}`}
                        className={`w-full flex items-center px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                          location.pathname === child.path
                            ? 'bg-blue-50 text-blue-700'
                            : 'hover:bg-gray-50 text-gray-600'
                        }`}
                      >
                        {child.icon}
                        <span className="ml-2">{child.title}</span>
                      </button>
                    )
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-700">{username}</p>
              <p className="text-xs text-gray-500 capitalize">{role}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut size={18} />
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <header className="bg-white shadow-sm border-b px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(!sidebarOpen)} className="mr-4">
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </Button>
            <h2 className="text-2xl font-bold text-gray-800">Dashboard</h2>
            {username && <span className="ml-3 text-lg text-gray-500 font-medium">Hello {username}!</span>}
          </div>
          <div className="text-sm text-gray-500">
            {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </div>
        </header>

        {/* Dashboard Content */}
        <main className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="space-y-8">
              {/* Aabkari Times Stats */}
              <div>
                <h3 className="text-lg font-bold text-blue-700 mb-4 flex items-center">
                  <BookOpen size={24} className="mr-2" />
                  Aabkari Times - Members
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard
                    title="Active Members"
                    value={stats?.aabkari?.active || 0}
                    color="#10b981"
                    icon={<CheckCircle size={24} color="#10b981" />}
                  />
                  <StatCard
                    title="Inactive Members"
                    value={stats?.aabkari?.inactive || 0}
                    color="#6b7280"
                    icon={<XCircle size={24} color="#6b7280" />}
                  />
                  <StatCard
                    title="Expired Members"
                    value={stats?.aabkari?.expired || 0}
                    color="#ef4444"
                    icon={<AlertTriangle size={24} color="#ef4444" />}
                  />
                  <StatCard
                    title="Expiring in 3 Months"
                    value={stats?.aabkari?.expiring_soon || 0}
                    color="#f59e0b"
                    icon={<Clock size={24} color="#f59e0b" />}
                  />
                </div>
              </div>

              {/* Sugar Times Stats */}
              <div>
                <h3 className="text-lg font-bold text-green-700 mb-4 flex items-center">
                  <BookOpen size={24} className="mr-2" />
                  Sugar Times - Members
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard
                    title="Active Members"
                    value={stats?.sugar?.active || 0}
                    color="#10b981"
                    icon={<CheckCircle size={24} color="#10b981" />}
                  />
                  <StatCard
                    title="Inactive Members"
                    value={stats?.sugar?.inactive || 0}
                    color="#6b7280"
                    icon={<XCircle size={24} color="#6b7280" />}
                  />
                  <StatCard
                    title="Expired Members"
                    value={stats?.sugar?.expired || 0}
                    color="#ef4444"
                    icon={<AlertTriangle size={24} color="#ef4444" />}
                  />
                  <StatCard
                    title="Expiring in 3 Months"
                    value={stats?.sugar?.expiring_soon || 0}
                    color="#f59e0b"
                    icon={<Clock size={24} color="#f59e0b" />}
                  />
                </div>
              </div>

              {/* Advertisers Stats */}
              <div>
                <h3 className="text-lg font-bold text-purple-700 mb-4 flex items-center">
                  <Building size={24} className="mr-2" />
                  Advertisers
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <StatCard
                    title="Aabkari Times - Active"
                    value={stats?.advertisers?.aabkari_active || 0}
                    subtitle="Active advertisers"
                    color="#2563eb"
                    icon={<CheckCircle size={24} color="#2563eb" />}
                  />
                  <StatCard
                    title="Aabkari Times - Inactive"
                    value={stats?.advertisers?.aabkari_inactive || 0}
                    subtitle="Inactive advertisers"
                    color="#6b7280"
                    icon={<XCircle size={24} color="#6b7280" />}
                  />
                  <StatCard
                    title="Sugar Times - Active"
                    value={stats?.advertisers?.sugar_active || 0}
                    subtitle="Active advertisers"
                    color="#059669"
                    icon={<CheckCircle size={24} color="#059669" />}
                  />
                  <StatCard
                    title="Sugar Times - Inactive"
                    value={stats?.advertisers?.sugar_inactive || 0}
                    subtitle="Inactive advertisers"
                    color="#6b7280"
                    icon={<XCircle size={24} color="#6b7280" />}
                  />
                </div>
              </div>

              {/* WhatsApp Magazine Broadcast Stats */}
              {waStats && (
                <div>
                  <h3 className="text-lg font-bold text-green-600 mb-4 flex items-center">
                    <MessageCircle size={24} className="mr-2" />
                    WhatsApp Magazine Broadcasts
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Aabkari Times WhatsApp */}
                    <div className="border rounded-lg p-4 bg-blue-50">
                      <h4 className="font-semibold text-blue-700 mb-3">Aabkari Times</h4>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="text-center p-2 bg-white rounded shadow-sm">
                          <div className="text-2xl font-bold text-blue-600">{waStats.aabkari?.sent || 0}</div>
                          <div className="text-xs text-gray-500">Sent</div>
                        </div>
                        <div className="text-center p-2 bg-white rounded shadow-sm">
                          <div className="text-2xl font-bold text-green-600">{waStats.aabkari?.delivered || 0}</div>
                          <div className="text-xs text-gray-500">Delivered</div>
                        </div>
                        <div className="text-center p-2 bg-white rounded shadow-sm">
                          <div className="text-2xl font-bold text-red-600">{waStats.aabkari?.failed || 0}</div>
                          <div className="text-xs text-gray-500">Failed</div>
                        </div>
                      </div>
                    </div>
                    {/* Sugar Times WhatsApp */}
                    <div className="border rounded-lg p-4 bg-green-50">
                      <h4 className="font-semibold text-green-700 mb-3">Sugar Times</h4>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="text-center p-2 bg-white rounded shadow-sm">
                          <div className="text-2xl font-bold text-blue-600">{waStats.sugar?.sent || 0}</div>
                          <div className="text-xs text-gray-500">Sent</div>
                        </div>
                        <div className="text-center p-2 bg-white rounded shadow-sm">
                          <div className="text-2xl font-bold text-green-600">{waStats.sugar?.delivered || 0}</div>
                          <div className="text-xs text-gray-500">Delivered</div>
                        </div>
                        <div className="text-center p-2 bg-white rounded shadow-sm">
                          <div className="text-2xl font-bold text-red-600">{waStats.sugar?.failed || 0}</div>
                          <div className="text-xs text-gray-500">Failed</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div>
                <h3 className="text-lg font-bold text-gray-700 mb-4 flex items-center">
                  <TrendingUp size={24} className="mr-2" />
                  Quick Actions
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  <Button 
                    variant="outline" 
                    className="h-20 flex flex-col items-center justify-center gap-2"
                    onClick={() => navigate('/register')}
                  >
                    <User size={24} className="text-blue-600" />
                    <span className="text-xs">New Member</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-20 flex flex-col items-center justify-center gap-2"
                    onClick={() => navigate('/register-advertiser')}
                  >
                    <Building size={24} className="text-green-600" />
                    <span className="text-xs">New Advertiser</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-20 flex flex-col items-center justify-center gap-2"
                    onClick={() => navigate('/scan-dispatch')}
                  >
                    <Scan size={24} className="text-purple-600" />
                    <span className="text-xs">Scan Dispatch</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-20 flex flex-col items-center justify-center gap-2"
                    onClick={() => navigate('/monthly-magazine-email')}
                  >
                    <Mail size={24} className="text-red-600" />
                    <span className="text-xs">Send Email</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-20 flex flex-col items-center justify-center gap-2"
                    onClick={() => navigate('/whatsapp-template-send')}
                  >
                    <MessageCircle size={24} className="text-green-500" />
                    <span className="text-xs">WhatsApp Send</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    className="h-20 flex flex-col items-center justify-center gap-2"
                    onClick={() => navigate('/print-label')}
                  >
                    <Tag size={24} className="text-orange-600" />
                    <span className="text-xs">Print Labels</span>
                  </Button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default DashboardNew;
